/*global swal*/
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import SweetAlert from 'react-swal';

import {
	loadProvidingVirtualProviders, loadReceivingVirtualProviders,
	addVirtualProvider, requestVirtualProvider, approveVirtualProvider, removeVirtualProvider,
} from './api';
import Practices from './Practices';
import { Icon, Button } from '../../../../components';

export default class VirtualProviders extends React.Component {
	static propTypes = {
		user: PropTypes.object.isRequired,
		showNotification: PropTypes.func
	}

	state = {
		providingProviders: [],
		receivingProviders: [],
		providingPractices: [],
		showSwal: false,
		showLinkModal: false
	}

	async componentDidMount() {
		this.refresh();
	}

	refresh = async () => {
		this.setState({ isLoading: true });
		const { data } = await loadReceivingVirtualProviders(this.props.user.id);
		if (data && data.status) {
			this.setState({ receivingProviders: data.status });
		}
		const result = await loadProvidingVirtualProviders(this.props.user.id);
		if (result && result.data && result.data.status) {
			this.setState({ providingProviders: result.data.status });
		}
		this.setState({ isLoading: false });
	}

	handleAddVirtualProvider = async ({ target }) => {
		const { provider_practice_virtual_id, provider_id } = target.dataset;
		const { data } = await addVirtualProvider(this.props.user.id, provider_id, provider_practice_virtual_id);
		if (data.status && data.status.added_to_practice) {
			this.setState({ isLoading: true });
			const { data } = await loadProvidingVirtualProviders(this.props.user.id);
			if (data && data.status) {
				this.setState({ providingProviders: data.status });
			}
			this.props.showNotification({ message: 'Virtual Provider Added', status: 'success', timeout: 3000 });
		} else {
			this.props.showNotification({ message: 'Unable to add Virtual Provider', status: 'danger', timeout: 10000 });
		}
		this.setState({ isLoading: false });
	}

	handleRequestVirtualProvider = async ({ target }) => {
		const { provider_virtual_id } = target.dataset;
		const { data } = await requestVirtualProvider(this.props.user.id, provider_virtual_id);
		if (data.status && data.status.requested) {
			this.setState({ isLoading: true });
			const { data } = await loadReceivingVirtualProviders(this.props.user.id);
			if (data && data.status) {
				this.setState({ receivingProviders: data.status });
			}
			this.props.showNotification({ message: 'Request Successful', status: 'success', timeout: 3000 });
		} else {
			this.props.showNotification({ message: 'Request failed!', status: 'danger', timeout: 10000 });
		}
		this.setState({ isLoading: false });
	}

	handleApproveVirtualProvider = async ({ target }) => {
		const { provider_virtual_id } = target.dataset;
		const { data } = await approveVirtualProvider(this.props.user.id, provider_virtual_id);
		if (data.status && data.status.approved) {
			this.setState({ isLoading: true });
			const { data } = await loadProvidingVirtualProviders(this.props.user.id);
			if (data && data.status) {
				this.setState({ providingProviders: data.status });
			}
			this.props.showNotification({ message: 'Virtual Provider Approved', status: 'success', timeout: 3000 });
		} else {
			this.props.showNotification({ message: 'Unable to approve Virtual Provider', status: 'danger', timeout: 10000 });
		}
		this.setState({ isLoading: false });
	}

	handleShowRemoveVirtualProviderWarning = ({ currentTarget }) => {
		const { provider_virtual_id, type } = currentTarget.dataset;
		this.setState({
			showSwal: true,
			provider_virtual_id,
			removingType: type
		});
	}

	handleRemoveVirtualProvider = async (isConfirm) => {
		if (isConfirm) {
			const { provider_virtual_id, removingType } = this.state;
			const { data } = await removeVirtualProvider(this.props.user.id, provider_virtual_id);
			if (data && data.status && data.status.removed) {
				this.setState({ isLoading: true });
				if (removingType === 'providing') {
					this.props.showNotification({ message: 'Providing Virtual Provider Unlinked', status: 'success', timeout: 3000 });
					const { data } = await loadProvidingVirtualProviders(this.props.user.id);
					if (data && data.status) {
						this.setState({ providingProviders: data.status });
					}
				} else {
					this.props.showNotification({ message: 'Receiving Virtual Provider Unlinked', status: 'success', timeout: 3000 });
					const { data } = await loadReceivingVirtualProviders(this.props.user.id);
					if (data && data.status) {
						this.setState({ receivingProviders: data.status });
					}
				}
			} else {
				this.props.showNotification({ message: 'Unable to remove Virtual Provider', status: 'danger', timeout: 10000 });
			}
		}
		swal.close();
		this.setState({
			showSwal: false,
			provider_virtual_id: undefined,
			removingType: undefined,
			isLoading: false
		});
	}

	render() {
		const { isLoading, providingProviders, receivingProviders, showSwal, removingType } = this.state;

		const message = removingType === 'providing'
			? 'This will completely remove the provider from the receiving practice.'
			: 'This will remove the virtual provider from the list.';

		return (
			<React.Fragment>
				<SweetAlert
					isOpen={showSwal}
					type="warning"
					title="Are you sure?"
					text={message}
					showCancelButton
					confirmButtonColor="#DD6B55"
					confirmButtonText="Yes"
					closeOnConfirm={false}
					closeOnCancel={false}
					callback={this.handleRemoveVirtualProvider}
				/>
				<Practices
					adminId={this.props.user.id}
					showNotification={this.props.showNotification}
					onChange={this.refresh}
				/>
				<div className="row">
					<div className="col-md-6">
						<div className="panel panel-default">
							<div className="panel-heading">
								Providing Providers
							</div>
							<div className="table-responsive">
								<table className={cn('table', 'table-bordered', 'table-hover', { 'whirl': isLoading, 'traditional': isLoading })}>
									<thead>
										<tr>
											<th>Provider</th>
											<th>Providing To</th>
											<th>Status</th>
										</tr>
									</thead>
									<tbody>
										{providingProviders.map(({
											provider_virtual_id, name_first, name_last, credentials, practice_name,
											added_provider, requested_provider, approved_provider, provider_id, provider_practice_virtual_id
										}) => (
											<tr key={`${provider_practice_virtual_id}-${provider_id}`}>
												<td>{name_first} {name_last}, {credentials}</td>
												<td>{practice_name}</td>
												<td>
													{Boolean(approved_provider) && (
														<React.Fragment>
															<span>
																<Icon name="fa-check" className="text-success" /> Embedded w/{practice_name}
															</span>
															<div className="pull-right">
																<Button
																	danger
																	icon="close"
																	data-provider_virtual_id={provider_virtual_id}
																	onClick={this.handleShowRemoveVirtualProviderWarning}
																	data-type="providing"
																/>
															</div>
														</React.Fragment>
													)}
													{(Boolean(requested_provider) && !approved_provider) && (
														<Button
															primary
															title="Approve for Embedding"
															data-provider_virtual_id={provider_virtual_id}
															onClick={this.handleApproveVirtualProvider}
														/>
													)}
													{(Boolean(added_provider) && !requested_provider) && (
														<span>
															<Icon name="fa-clock-o" className="text-info" /> Awaiting Request
														</span>
													)}
													{!added_provider && (
														<Button
															info
															name={provider_virtual_id}
															title="Make Available"
															data-provider_id={provider_id}
															data-provider_practice_virtual_id={provider_practice_virtual_id}
															onClick={this.handleAddVirtualProvider}
														/>
													)}
												</td>
											</tr>
										))}
										{providingProviders.length === 0 && (
											<tr>
												<td colSpan="3">0 records found</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
					<div className="col-md-6">
						<div className="panel panel-default">
						<div className="panel-heading">Receiving Providers</div>
							<div className="table-responsive">
								<table className={cn('table', 'table-bordered', 'table-hover', { 'whirl': isLoading, 'traditional': isLoading })}>
									<thead>
										<tr>
											<th>Provider</th>
											<th>Receiving From</th>
											<th>Status</th>
										</tr>
									</thead>
									<tbody>
										{receivingProviders.map(({ provider_virtual_id, name_first, name_last, credentials, practice_name, requested_provider, approved_provider }) => (
											<tr key={provider_virtual_id}>
												<td>{name_first} {name_last}, {credentials}</td>
												<td>{practice_name}</td>
												<td>
													{!requested_provider && (
														<Button
															title="Request Provider"
															onClick={this.handleRequestVirtualProvider}
															info
															data-provider_virtual_id={provider_virtual_id}
														/>
													)}
													{(Boolean(requested_provider) && !approved_provider) && (
														<span>
															<Icon name="fa-clock-o" className="text-info" /> Awaiting Approval
														</span>
													)}
													{Boolean(approved_provider) && (
														<React.Fragment>
															<div className="pull-right">
																<Button
																	danger
																	icon="close"
																	data-provider_virtual_id={provider_virtual_id}
																	onClick={this.handleShowRemoveVirtualProviderWarning}
																	data-type="receiving"
																/>
															</div>
															<span>
																<Icon name="fa-check" className="text-success" /> Available for Visits
															</span>
														</React.Fragment>
													)}
												</td>
											</tr>
										))}
										{receivingProviders.length === 0 && (
											<tr>
												<td colSpan="3">0 records found</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</React.Fragment>
		);
	}
}
